import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/email_sending_preferences';

export const EmailSendingPreferencesSchema = z.object({
  sendCopy: z.boolean(),
  sendCopyEmails: z.array(z.email()),
  sendToAllEmails: z.boolean(),
  sendToContactPersonAndDebtorEmail: z.boolean(),
  // The local part of the sending address. Empty means "not configured" — the
  // backend renders "" for a null column so the value is never nullable here.
  defaultFrom: z.string(),
});

export type EmailSendingPreferences = z.infer<typeof EmailSendingPreferencesSchema>;

export async function loadEmailSendingPreferencesQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = EmailSendingPreferencesSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'emailSendingPreferences');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadEmailSendingPreferences(
  options: QueryOptions<EmailSendingPreferences> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<EmailSendingPreferences, ApiError>({
    queryKey: ['emailSendingPreferences'],
    queryFn: () => loadEmailSendingPreferencesQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<EmailSendingPreferences, ApiError, 'emailSendingPreferences'>(
    'emailSendingPreferences',
    queryResult,
  );
}
